import { useState } from 'react'
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Button,
  useDisclosure,
} from '@chakra-ui/react'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import useTranslation from 'next-translate/useTranslation'
import { ExtendedNewsletterForm } from '@forms'
import { IFonts } from '@interfaces'

type NewsletterFormInputs = {
  email: string
}
interface INewsletterForm {
  buttonLabel: string
  activeFontFamily?: IFonts
}

const NewsletterForm = ({
  buttonLabel,
  activeFontFamily = 'body',
}: INewsletterForm) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const { isOpen, onClose, onToggle } = useDisclosure()

  const schema = yup.object().shape({
    email: yup
      .string()
      .email(t('form:error.invalidEmail'))
      .required(t('form:error.requiredEmail')),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterFormInputs>({
    resolver: yupResolver(schema),
  })

  const onSubmit = async (values: NewsletterFormInputs) => {
    setEmail(values.email)
    onToggle()
    reset()
  }

  return (
    <Flex
      as="form"
      position="relative"
      mt="4"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      w="full"
      alignItems="flex-end"
    >
      <Box flex="1" pb="8">
        <FormControl isInvalid={!!errors?.email?.message}>
          <Input
            type="email"
            variant="flushed"
            placeholder={t('form:field.email')}
            focusBorderColor="black.primary3"
            errorBorderColor="red.primary"
            _placeholder={{
              color: 'grey.primary2',
              fontSize: 'sm',
            }}
            fontFamily={activeFontFamily}
            {...register('email')}
            borderBottomColor="black.primary5"
          />
          <FormErrorMessage color="red.primary" fontFamily={activeFontFamily}>
            {errors?.email?.message}
          </FormErrorMessage>
        </FormControl>
      </Box>
      <Button
        ml="4"
        onClick={handleSubmit(onSubmit)}
        size="newsletter"
        variant="secondary"
        isDisabled={!!errors?.email}
        fontFamily={activeFontFamily}
        whiteSpace="normal"
        lineHeight="1.2"
        _hover={{ backgroundColor: 'red.primary3' }}
      >
        {buttonLabel}
      </Button>
      <ExtendedNewsletterForm
        isOpen={isOpen}
        onClose={onClose}
        email={email}
        newsLetterReset={reset}
        activeFontFamily={activeFontFamily}
      />
    </Flex>
  )
}

export default NewsletterForm
